/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import { Injectable, Injector } from '@angular/core';
import { AviPermissionResource } from 'generated-types';
import { AUTHENTICATED } from 'state-names';
import * as UIPermissions from 'ng/modules/core/constants/ui-permissions.constants';
import { getParentStateName } from 'ng/modules/core/utils/router-states.utils';

import {
    IState,
    IStatePermissionData,
    stateTree,
} from 'ng/modules/core/constants/state-tree.constants';

import { IStateAccessMap } from 'ajs/js/services/appStates.types';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';

export const STATE_ACCESS_SERVICE_TOKEN = 'stateAccessService';

/**
 * Set of UI-only permissions.
 */
const uiPermissionsSet = new Set(Object.keys(UIPermissions));

/**
 * Create a hash of state names to permissions based on the state-tree constant.
 */
const createStatePermissionHash = (
    states: IState[],
): Record<string, AviPermissionResource | string | IStatePermissionData> => {
    return states.reduce((acc, state) => {
        const {
            permission,
            children = [],
            name,
        } = state;

        if (permission) {
            acc[name] = permission;
        }

        if (children.length) {
            acc = {
                ...acc,
                ...createStatePermissionHash(children),
            };
        }

        return acc;
    }, {});
};

/**
 * @description
 *      Service to Generate a map of state names to its accessibility
 *      by the current user.
 *      stateAccessMap is regenerated on each tenant switch.
 *      Used by StateManagerService to validate a state.
 * @author Aravindh Nagarajan, alextsg
 */
@Injectable({
    providedIn: 'root',
})
export class StateAccessService {
    private statePermissionHash = createStatePermissionHash(stateTree);

    /**
     * Map of UI-only permissions to a set of AviPermissionResource permissions.
     */
    private uiPermissionsMap = this.createUIPermissionMapping();

    /**
     * Keeps the generated access map, which is a Hash of state names
     * and its accessiblity by the current user. will get regenerated on
     * every tenant switch. Used by stateManagerService.
     */
    private stateAccessMap: IStateAccessMap = {};

    constructor(private readonly $injector: Injector) {}

    /**
     * Return the permission associated with a state name.
     */
    public getPermissionFromStateName(stateName: string): string | AviPermissionResource {
        let permission = this.statePermissionHash[stateName];

        if (typeof permission !== 'string') {
            permission = permission?.permission;
        }

        return permission;
    }

    /**
     * Return the uiPermissionsMap, a map of UI-only permissions to a set of AviPermissionResource
     * permissions.
     */
    public getUIPermissionsMap(): Map<string, Set<string>> {
        return this.uiPermissionsMap;
    }

    /**
     * Check whether user has access to the state path. If the stateAccessMap does not contain the
     * state name, return the parent state's access. A state that does not have a permission
     * requirements inherits the permission requirement from its parent.
     */
    public isAccessible(stateName: string): boolean {
        const { stateAccessMap } = this;

        if (this.stateAccessIsDefined(stateName)) {
            return stateAccessMap[stateName];
        }

        // Return true if state is the top level state.
        if (stateName === AUTHENTICATED) {
            return true;
        }

        const parentStateName = getParentStateName(stateName);

        // Return true if parent is the top level state.
        if (parentStateName === AUTHENTICATED) {
            return true;
        }

        return this.isAccessible(parentStateName);
    }

    /**
     * Generate stateAccessMap by parsing statePermissionHash.
     */
    public generateMap(): void {
        const auth = this.$injector.get(Auth);

        // Set stateAccessMap based on statePermissionHash.
        this.stateAccessMap = Object.keys(this.statePermissionHash).reduce((acc, stateName) => {
            const value = this.statePermissionHash[stateName];

            if (typeof value === 'string') {
                return {
                    ...acc,
                    [stateName]: auth.isPrivilegeAllowed(value),
                };
            } else {
                const {
                    permission,
                    restrictAdminTenant = false,
                    restrictAllTenants = false,
                } = value;

                switch (true) {
                    case restrictAdminTenant && auth.isAdminTenant():
                    case restrictAllTenants && auth.allTenantsMode():
                        return {
                            ...acc,
                            [stateName]: false,
                        };
                    default:
                        return {
                            ...acc,
                            [stateName]: auth.isPrivilegeAllowed(permission),
                        };
                }
            }
        }, {});
    }

    /**
     * Return true if the state has a permission defined in the stateAccessMap.
     */
    private stateAccessIsDefined(stateName: string): boolean {
        return stateName in this.stateAccessMap;
    }

    /**
     * Create a map of UI-only permissions to a set of AviPermissionResource permissions.
     */
    private createUIPermissionMapping(): Map<string, Set<string>> {
        const permissionMap = new Map();

        Object.keys(this.statePermissionHash).forEach(stateName => {
            let stateNameParts = stateName.split('.');

            // If stateNameParts.length is 2 then it's a root state (applications, operations, etc.)
            // and we can skip this because we don't need to set childStates for their parent state
            // (authenticated) as it should always return true.
            while (stateNameParts.length > 2) {
                const parentName = stateNameParts.slice(0, stateNameParts.length - 1).join('.');
                const parentPermission = this.getPermissionFromStateName(parentName);

                // Parent state is already defined in the permissionHash, so we don't need to create
                // a custom/consolidated check for the parent state.
                if (!uiPermissionsSet.has(parentPermission)) {
                    return;
                }

                const childPermission = this.getPermissionFromStateName(stateName);

                if (!uiPermissionsSet.has(childPermission)) {
                    if (permissionMap.has(parentPermission)) {
                        const childPermissionsSet = permissionMap.get(parentPermission);

                        childPermissionsSet.add(childPermission);
                    } else {
                        permissionMap.set(parentPermission, new Set([childPermission]));
                    }
                }

                stateNameParts = stateNameParts.slice(0, stateNameParts.length - 1);
            }
        });

        return permissionMap;
    }
}
